import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Upload, FileText, X, Play, Square, ChevronDown, ChevronRight,
  CheckCircle, AlertCircle, AlertTriangle, HelpCircle,
  Folder, FolderOpen, BookMarked, ScrollText, Loader2,
  BarChart2, Download,
} from "lucide-react";
import DashboardTopbar from "@/components/layout/DashboardTopbar";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { firestore } from "@/lib/firebase";
import { collection, onSnapshot } from "firebase/firestore";
import { Markdown } from "@/components/Markdown";
import { geminiProxy } from "@/lib/backendAI";
import type { Framework, Rule } from "./ComplianceGarden";

// ─── Gemini API ───────────────────────────────────────────────────────────────

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const STATUS_INSTRUCTION = `

At the end of your response output exactly one of the following on its own line:
COMPLIANCE STATUS: Compliant
COMPLIANCE STATUS: Check
COMPLIANCE STATUS: Non-Compliant
COMPLIANCE STATUS: Data Unavailable`;

type ComplianceStatus = "Compliant" | "Check" | "Non-Compliant" | "Data Unavailable";

interface RuleResult {
  ruleId: string;
  ruleTitle: string;
  reference?: string;
  status: ComplianceStatus;
  details: string;
}

function parseStatus(text: string): ComplianceStatus {
  const m = text.match(/COMPLIANCE STATUS:\s*(Compliant|Check|Non-Compliant|Data Unavailable)/i);
  if (!m) {
    // fallback heuristic
    const low = text.toLowerCase();
    if (low.includes("non-compliant") || low.includes("non compliant")) return "Non-Compliant";
    if (low.includes("data unavailable") || low.includes("not available")) return "Data Unavailable";
    if (low.includes("check") || low.includes("concern") || low.includes("further review")) return "Check";
    if (low.includes("compliant")) return "Compliant";
    return "Check";
  }
  return m[1] as ComplianceStatus;
}

async function analyzeRule(
  rule: Rule,
  pdfBase64: string,
  signal: AbortSignal
): Promise<RuleResult> {
  const basePrompt = rule.prompt?.trim()
    ? rule.prompt.trim()
    : `You are a compliance verification expert. Analyse this financial document and check compliance with the following rule:

Rule: ${rule.title}
Description: ${rule.description}${rule.reference ? `\nReference: ${rule.reference}` : ""}

Provide a thorough analysis. Identify specific evidence in the document that confirms or contradicts compliance. If the required information is not present in the document, note it explicitly.`;

  // Ensure status instruction is appended exactly once
  const fullPrompt = basePrompt.includes("COMPLIANCE STATUS:") ? basePrompt : basePrompt + STATUS_INSTRUCTION;

  const data = await geminiProxy("gemini-2.5-flash", {
    contents: [{
      role: "user",
      parts: [
        { inlineData: { mimeType: "application/pdf", data: pdfBase64 } },
        { text: fullPrompt },
      ],
    }],
    generationConfig: { temperature: 0.1, maxOutputTokens: 2048 },
  }, signal);
  const text: string = data?.candidates?.[0]?.content?.parts?.[0]?.text || "";
  const status = parseStatus(text);
  const details = text.replace(/COMPLIANCE STATUS:\s*(Compliant|Check|Non-Compliant|Data Unavailable)\s*/gi, "").trim();

  return { ruleId: rule.id, ruleTitle: rule.title, reference: rule.reference, status, details };
}

// ─── Status config ────────────────────────────────────────────────────────────

const statusCfg: Record<ComplianceStatus, { icon: React.ReactNode; badge: string; border: string; bg: string }> = {
  "Compliant":        { icon: <CheckCircle    className="h-4 w-4" />, badge: "bg-green-600 text-white",  border: "border-green-200", bg: "bg-green-50"  },
  "Check":            { icon: <AlertTriangle  className="h-4 w-4" />, badge: "bg-orange-500 text-white", border: "border-orange-200",bg: "bg-orange-50" },
  "Non-Compliant":    { icon: <AlertCircle    className="h-4 w-4" />, badge: "bg-red-600 text-white",    border: "border-red-200",   bg: "bg-red-50"    },
  "Data Unavailable": { icon: <HelpCircle     className="h-4 w-4" />, badge: "bg-gray-500 text-white",   border: "border-gray-200",  bg: "bg-gray-50"   },
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ChecklistRunner: React.FC = () => {
  const { toast } = useToast();

  // Firestore data
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [loadingFw,  setLoadingFw]  = useState(true);

  // Rule selection
  const [selectedRuleIds, setSelectedRuleIds] = useState<Set<string>>(new Set());
  const [openFw,  setOpenFw]  = useState<Set<string>>(new Set());
  const [openCh,  setOpenCh]  = useState<Set<string>>(new Set());
  const [search,  setSearch]  = useState("");

  // PDF
  const [pdfFile,   setPdfFile]   = useState<File | null>(null);
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Analysis
  const [running,   setRunning]   = useState(false);
  const [progress,  setProgress]  = useState({ completed: 0, total: 0, current: "" });
  const [results,   setResults]   = useState<RuleResult[]>([]);
  const [expandedResults, setExpandedResults] = useState<Set<string>>(new Set());
  const abortRef = useRef<AbortController | null>(null);

  // Load frameworks from Firestore
  useEffect(() => {
    const unsub = onSnapshot(collection(firestore, "compliance_garden"), snap => {
      const fws: Framework[] = [];
      snap.forEach(d => fws.push(d.data() as Framework));
      fws.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      setFrameworks(fws);
      setLoadingFw(false);
    }, err => {
      toast({ title: "Failed to load compliance rules", description: err.message, variant: "destructive" });
      setLoadingFw(false);
    });
    return unsub;
  }, []);

  // Helpers
  const allRules: Rule[] = frameworks.flatMap(f => f.chapters.flatMap(c => c.rules));
  const selectedRules = allRules.filter(r => selectedRuleIds.has(r.id));

  const toggleRule = (id: string) => setSelectedRuleIds(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const toggleFw  = (id: string) => setOpenFw(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleCh  = (id: string) => setOpenCh(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const selectAllInChapter = (rules: Rule[]) => setSelectedRuleIds(prev => {
    const n = new Set(prev);
    rules.forEach(r => n.add(r.id));
    return n;
  });
  const deselectAllInChapter = (rules: Rule[]) => setSelectedRuleIds(prev => {
    const n = new Set(prev);
    rules.forEach(r => n.delete(r.id));
    return n;
  });

  const selectAllInFramework = (fw: Framework) => {
    const ruleIds = fw.chapters.flatMap(c => c.rules.map(r => r.id));
    setSelectedRuleIds(prev => { const n = new Set(prev); ruleIds.forEach(id => n.add(id)); return n; });
  };
  const deselectAllInFramework = (fw: Framework) => {
    const ruleIds = fw.chapters.flatMap(c => c.rules.map(r => r.id));
    setSelectedRuleIds(prev => { const n = new Set(prev); ruleIds.forEach(id => n.delete(id)); return n; });
  };

  const clearAll = () => setSelectedRuleIds(new Set());

  // File upload
  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") { toast({ title: "PDF required", variant: "destructive" }); return; }
    setPdfFile(file);
    setPdfBase64(null);
    try {
      const b64 = await fileToBase64(file);
      setPdfBase64(b64);
    } catch { toast({ title: "Failed to read PDF", variant: "destructive" }); }
  };

  // Run analysis
  const runAnalysis = async () => {
    if (!pdfBase64)           { toast({ title: "Upload a PDF first",           variant: "destructive" }); return; }
    if (selectedRules.length === 0) { toast({ title: "Select at least one rule", variant: "destructive" }); return; }

    setRunning(true);
    setResults([]);
    setExpandedResults(new Set());
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setProgress({ completed: 0, total: selectedRules.length, current: "" });

    const collected: RuleResult[] = [];

    for (let i = 0; i < selectedRules.length; i++) {
      if (ctrl.signal.aborted) break;
      const rule = selectedRules[i];
      setProgress({ completed: i, total: selectedRules.length, current: rule.title });
      try {
        const result = await analyzeRule(rule, pdfBase64, ctrl.signal);
        collected.push(result);
        setResults([...collected]);
      } catch (e: any) {
        if (e?.name === "AbortError") break;
        collected.push({
          ruleId: rule.id, ruleTitle: rule.title, reference: rule.reference,
          status: "Data Unavailable",
          details: `Analysis failed: ${String(e)}`,
        });
        setResults([...collected]);
      }
    }

    setProgress(p => ({ ...p, completed: collected.length, current: "" }));
    setRunning(false);
    abortRef.current = null;
    toast({ title: "Analysis complete", description: `${collected.length} rules analysed` });
  };

  const stopAnalysis = () => {
    abortRef.current?.abort();
    setRunning(false);
    toast({ title: "Analysis stopped" });
  };

  // Results summary
  const summary = {
    Compliant:        results.filter(r => r.status === "Compliant").length,
    Check:            results.filter(r => r.status === "Check").length,
    "Non-Compliant":  results.filter(r => r.status === "Non-Compliant").length,
    "Data Unavailable": results.filter(r => r.status === "Data Unavailable").length,
  };

  // Download results as HTML
  const downloadHTML = () => {
    if (!results.length) return;
    const escape = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>");
    const cards = results.map(r => {
      const cfg = statusCfg[r.status];
      const colors: Record<ComplianceStatus, string> = {
        "Compliant": "#16a34a", "Check": "#ea580c", "Non-Compliant": "#dc2626", "Data Unavailable": "#6b7280"
      };
      return `<div style="background:#fff;border:1px solid #e5e7eb;border-radius:10px;margin-bottom:14px;overflow:hidden">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;padding:14px 18px;border-bottom:1px solid #f3f4f6">
    <div>
      <div style="font-size:14px;font-weight:600;color:#111827">${escape(r.ruleTitle)}</div>
      ${r.reference ? `<span style="font-size:11px;color:#6b7280;font-family:monospace;border:1px solid #e5e7eb;border-radius:4px;padding:1px 6px">${escape(r.reference)}</span>` : ""}
    </div>
    <span style="font-size:12px;font-weight:600;padding:4px 12px;border-radius:999px;white-space:nowrap;background:${colors[r.status]};color:#fff">${r.status}</span>
  </div>
  <details style="padding:14px 18px"><summary style="cursor:pointer;font-size:13px;font-weight:600;color:#374151">View Analysis</summary>
    <div style="margin-top:10px;font-size:13px;color:#374151;line-height:1.7">${escape(r.details)}</div>
  </details>
</div>`;
    }).join("\n");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Compliance Checklist Report</title></head>
<body style="font-family:system-ui,sans-serif;background:#f9fafb;padding:24px;color:#1f2937">
<div style="max-width:900px;margin:0 auto">
<h1 style="font-size:24px;font-weight:700;margin-bottom:4px">Compliance Checklist Report</h1>
<p style="color:#6b7280;font-size:13px;margin-bottom:20px">Generated: ${new Date().toLocaleString()} · Document: ${pdfFile?.name || "Unknown"}</p>
<div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:20px">
  <div style="background:#f0fdf4;border:1px solid #dcfce7;border-radius:8px;padding:14px;text-align:center"><div style="font-size:22px;font-weight:700;color:#16a34a">${summary.Compliant}</div><div style="font-size:11px;color:#6b7280">Compliant</div></div>
  <div style="background:#fff7ed;border:1px solid #fed7aa;border-radius:8px;padding:14px;text-align:center"><div style="font-size:22px;font-weight:700;color:#ea580c">${summary.Check}</div><div style="font-size:11px;color:#6b7280">Check</div></div>
  <div style="background:#fef2f2;border:1px solid #fecaca;border-radius:8px;padding:14px;text-align:center"><div style="font-size:22px;font-weight:700;color:#dc2626">${summary["Non-Compliant"]}</div><div style="font-size:11px;color:#6b7280">Non-Compliant</div></div>
  <div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:14px;text-align:center"><div style="font-size:22px;font-weight:700;color:#6b7280">${summary["Data Unavailable"]}</div><div style="font-size:11px;color:#6b7280">Data Unavailable</div></div>
</div>
${cards}
</div></body></html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `compliance-checklist-${Date.now()}.html`;
    a.click(); URL.revokeObjectURL(url);
  };

  // Filtered frameworks
  const q = search.toLowerCase();
  const filteredFws = frameworks.filter(fw =>
    !q ||
    fw.title.toLowerCase().includes(q) ||
    fw.chapters.some(ch =>
      ch.title.toLowerCase().includes(q) ||
      ch.rules.some(r => r.title.toLowerCase().includes(q))
    )
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardTopbar />
      <div className="pt-20 px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-7xl mx-auto space-y-4">

          {/* Header */}
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <BarChart2 className="h-8 w-8 text-indigo-600" />
              Compliance Checklist Runner
            </h1>
            <p className="text-gray-500 mt-1 text-sm">
              Select rules from your Compliance Garden, upload a document, and run AI analysis
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

            {/* ── LEFT: Rule Selector ──────────────────────────────────── */}
            <div className="lg:col-span-2 space-y-3">
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold">Select Rules</CardTitle>
                    <div className="flex gap-1.5">
                      <Badge variant="outline" className="text-xs">{selectedRuleIds.size} selected</Badge>
                      {selectedRuleIds.size > 0 && (
                        <button onClick={clearAll} className="text-xs text-red-500 hover:text-red-700 underline">Clear</button>
                      )}
                    </div>
                  </div>
                  <input
                    className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 mt-2"
                    placeholder="Search rules…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </CardHeader>
                <CardContent className="p-0">
                  {loadingFw ? (
                    <div className="flex items-center justify-center py-10 gap-2 text-gray-400">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                    </div>
                  ) : filteredFws.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-8">No frameworks found. Create them in Compliance Garden.</p>
                  ) : (
                    <div className="max-h-[calc(100vh-340px)] overflow-y-auto">
                      {filteredFws.map(fw => {
                        const fwOpen = openFw.has(fw.id);
                        const fwRules = fw.chapters.flatMap(c => c.rules);
                        const fwSelected = fwRules.filter(r => selectedRuleIds.has(r.id)).length;
                        return (
                          <div key={fw.id} className="border-b border-gray-100 last:border-0">
                            {/* Framework row */}
                            <div className="flex items-center gap-2 px-4 py-2.5 hover:bg-gray-50 cursor-pointer select-none"
                              onClick={() => toggleFw(fw.id)}>
                              {fwOpen ? <FolderOpen className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <Folder className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                              <span className="flex-1 text-sm font-semibold text-gray-800 truncate">{fw.title}</span>
                              {fwSelected > 0 && <Badge variant="outline" className="text-xs text-indigo-600 border-indigo-200">{fwSelected}</Badge>}
                              <div className="flex gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                <button onClick={() => selectAllInFramework(fw)} className="text-[10px] text-indigo-600 hover:underline">All</button>
                                <span className="text-gray-300 text-[10px]">|</span>
                                <button onClick={() => deselectAllInFramework(fw)} className="text-[10px] text-gray-500 hover:underline">None</button>
                              </div>
                              {fwOpen ? <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />}
                            </div>

                            {fwOpen && fw.chapters.map(ch => {
                              const chOpen = openCh.has(ch.id);
                              const chSelected = ch.rules.filter(r => selectedRuleIds.has(r.id)).length;
                              return (
                                <div key={ch.id} className="border-t border-gray-50">
                                  {/* Chapter row */}
                                  <div className="flex items-center gap-2 pl-8 pr-4 py-2 hover:bg-gray-50 cursor-pointer select-none"
                                    onClick={() => toggleCh(ch.id)}>
                                    <BookMarked className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                    <span className="flex-1 text-xs font-medium text-gray-700 truncate">{ch.title}</span>
                                    {chSelected > 0 && <Badge variant="outline" className="text-[10px] text-indigo-500 border-indigo-200">{chSelected}</Badge>}
                                    <div className="flex gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                      <button onClick={() => selectAllInChapter(ch.rules)} className="text-[10px] text-indigo-600 hover:underline">All</button>
                                      <span className="text-gray-300 text-[10px]">|</span>
                                      <button onClick={() => deselectAllInChapter(ch.rules)} className="text-[10px] text-gray-500 hover:underline">None</button>
                                    </div>
                                    {chOpen ? <ChevronDown className="h-3 w-3 text-gray-400 flex-shrink-0" /> : <ChevronRight className="h-3 w-3 text-gray-400 flex-shrink-0" />}
                                  </div>

                                  {chOpen && ch.rules.map(rule => (
                                    <label key={rule.id}
                                      className={cn(
                                        "flex items-start gap-2.5 pl-14 pr-4 py-2 cursor-pointer hover:bg-indigo-50 transition-colors",
                                        selectedRuleIds.has(rule.id) && "bg-indigo-50"
                                      )}
                                    >
                                      <input
                                        type="checkbox"
                                        className="mt-0.5 accent-indigo-600 flex-shrink-0"
                                        checked={selectedRuleIds.has(rule.id)}
                                        onChange={() => toggleRule(rule.id)}
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="text-xs font-medium text-gray-800 leading-tight">{rule.title}</div>
                                        {rule.reference && (
                                          <span className="text-[10px] font-mono text-gray-400">{rule.reference}</span>
                                        )}
                                        {!rule.prompt && (
                                          <span className="text-[10px] text-amber-500 italic"> · no prompt</span>
                                        )}
                                      </div>
                                    </label>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* ── RIGHT: Upload + Run + Results ───────────────────────── */}
            <div className="lg:col-span-3 space-y-4">

              {/* PDF Upload */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold">Document to Analyse</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={handleFile} />
                  {pdfFile ? (
                    <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                      <FileText className="h-5 w-5 text-green-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-green-800 truncate">{pdfFile.name}</div>
                        <div className="text-xs text-green-600">{(pdfFile.size / 1024 / 1024).toFixed(1)} MB</div>
                      </div>
                      {!pdfBase64 && <Loader2 className="h-4 w-4 text-green-600 animate-spin" />}
                      {pdfBase64 && <CheckCircle className="h-4 w-4 text-green-600" />}
                      <button onClick={() => { setPdfFile(null); setPdfBase64(null); }} className="text-red-400 hover:text-red-600">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => fileRef.current?.click()}
                      className="w-full border-2 border-dashed border-gray-300 hover:border-indigo-400 rounded-lg py-8 flex flex-col items-center gap-2 text-gray-500 hover:text-indigo-600 transition-colors">
                      <Upload className="h-7 w-7" />
                      <span className="text-sm font-medium">Upload PDF document</span>
                      <span className="text-xs text-gray-400">Text-based PDF — annual report, financial statements, audit report…</span>
                    </button>
                  )}

                  {/* Run / Stop */}
                  <div className="flex gap-3">
                    <Button
                      className="flex-1"
                      disabled={running || !pdfBase64 || selectedRuleIds.size === 0}
                      onClick={runAnalysis}
                    >
                      {running
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analysing {progress.completed}/{progress.total}…</>
                        : <><Play className="h-4 w-4 mr-2" />Run Analysis ({selectedRuleIds.size} rule{selectedRuleIds.size !== 1 ? "s" : ""})</>
                      }
                    </Button>
                    {running && (
                      <Button variant="outline" onClick={stopAnalysis} className="text-red-600 border-red-300 hover:bg-red-50">
                        <Square className="h-4 w-4 mr-1.5" /> Stop
                      </Button>
                    )}
                    {results.length > 0 && !running && (
                      <Button variant="outline" onClick={downloadHTML}>
                        <Download className="h-4 w-4 mr-1.5" /> Export HTML
                      </Button>
                    )}
                  </div>

                  {/* Progress bar */}
                  {running && (
                    <div>
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span className="truncate max-w-xs">{progress.current}</span>
                        <span>{progress.completed}/{progress.total}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="h-2 rounded-full bg-indigo-500 transition-all"
                          style={{ width: progress.total > 0 ? `${(progress.completed / progress.total) * 100}%` : "0%" }} />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Results */}
              {results.length > 0 && (
                <div className="space-y-3">
                  {/* Summary cards */}
                  <div className="grid grid-cols-4 gap-3">
                    {(["Compliant", "Check", "Non-Compliant", "Data Unavailable"] as ComplianceStatus[]).map(s => (
                      <div key={s} className={cn("rounded-lg border p-3 text-center", statusCfg[s].border, statusCfg[s].bg)}>
                        <div className={cn("text-2xl font-bold", {
                          "text-green-700": s === "Compliant",
                          "text-orange-600": s === "Check",
                          "text-red-700":   s === "Non-Compliant",
                          "text-gray-600":  s === "Data Unavailable",
                        })}>{summary[s]}</div>
                        <div className="text-[11px] text-gray-500 mt-0.5">{s}</div>
                      </div>
                    ))}
                  </div>

                  {/* Result cards */}
                  {results.map(r => {
                    const cfg = statusCfg[r.status];
                    const isOpen = expandedResults.has(r.ruleId);
                    return (
                      <Card key={r.ruleId} className={cn("border", cfg.border)}>
                        <div
                          className={cn("flex items-start gap-3 p-4 cursor-pointer", cfg.bg)}
                          onClick={() => setExpandedResults(prev => {
                            const n = new Set(prev);
                            n.has(r.ruleId) ? n.delete(r.ruleId) : n.add(r.ruleId);
                            return n;
                          })}
                        >
                          <span className={cn("mt-0.5 flex-shrink-0", {
                            "text-green-600": r.status === "Compliant",
                            "text-orange-500": r.status === "Check",
                            "text-red-600": r.status === "Non-Compliant",
                            "text-gray-500": r.status === "Data Unavailable",
                          })}>{cfg.icon}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-gray-800">{r.ruleTitle}</span>
                              {r.reference && <Badge variant="outline" className="text-[10px] font-mono">{r.reference}</Badge>}
                            </div>
                          </div>
                          <Badge className={cn("text-xs flex-shrink-0", cfg.badge)}>{r.status}</Badge>
                          {isOpen ? <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                        </div>
                        {isOpen && (
                          <CardContent className="pt-3 pb-4">
                            <Markdown content={r.details} className="text-gray-700" />
                          </CardContent>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}

              {/* Empty state */}
              {!running && results.length === 0 && pdfBase64 && selectedRuleIds.size > 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400 text-sm gap-2">
                  <Play className="h-8 w-8" />
                  <span>Click "Run Analysis" to start</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChecklistRunner;
